<template>
  <div class="my-4 lg:my-8">
    <Collapsible
      :title="$t('pdp.manufacturer_details.headline')"
      additional-classes="text-xl font-medium"
    >
      <div class="my-6 text-base">
        <p class="mb-2">
          {{ $t('pdp.manufacturer_details.manufacturer_title') }}:
        </p>
        <p v-if="brand">
          {{ `${$t('pdp.manufacturer_details.manufacturer_brand')}: ${brand}` }}
        </p>
        <p v-if="legalManufacturer && manufacturerAddress">
          {{
            `${$t('pdp.manufacturer_details.manufacturer_name')}: ${legalManufacturer}, ${manufacturerAddress}`
          }}
        </p>
        <p v-if="manufacturerContact">
          {{ $t('pdp.manufacturer_details.manufacturer_contact') }}:
          <a
            :href="manufacturerHref"
            target="_blank"
            rel="noopener"
            class="underline"
          >
            {{ manufacturerContact }}
          </a>
        </p>
      </div>

      <div v-if="hasImporter" class="text-base">
        <p class="mb-2">{{ $t('pdp.manufacturer_details.importer_title') }}:</p>
        <p v-if="responsiblePersonEu && addressResponsiblePerson">
          {{
            `${$t('pdp.manufacturer_details.importer_address')}: ${responsiblePersonEu}, ${addressResponsiblePerson}`
          }}
        </p>
        <p v-if="contactResponsiblePerson">
          {{ $t('pdp.manufacturer_details.importer_contact') }}:
          <a
            :href="importerHref"
            target="_blank"
            rel="noopener"
            class="underline"
          >
            {{ contactResponsiblePerson }}
          </a>
        </p>
      </div>
    </Collapsible>
  </div>
</template>

<script setup lang="ts">
import { getFirstAttributeLabel } from '~/utils/attribute'
import type { Attributes } from '@scayle/storefront-nuxt'

const props = defineProps({
  productAttributes: {
    type: Object as PropType<Attributes>,
    required: true,
  },
})

// Manufacturer
const brand = getFirstAttributeLabel(props.productAttributes, 'brand')
const legalManufacturer = getFirstAttributeLabel(
  props.productAttributes,
  'legalManufacturer',
)
const manufacturerAddress = getFirstAttributeLabel(
  props.productAttributes,
  'manufacturerAddress',
)
const manufacturerContact = getFirstAttributeLabel(
  props.productAttributes,
  'manufacturerContact',
)
const contactTypeManufacturer = getFirstAttributeLabel(
  props.productAttributes,
  'contactTypeManufacturer',
)
const manufacturerHref = computed(() =>
  getContactHref(contactTypeManufacturer, manufacturerContact),
)

// Importer
const responsiblePersonEu = getFirstAttributeLabel(
  props.productAttributes,
  'responsiblePersonEu',
)
const addressResponsiblePerson = getFirstAttributeLabel(
  props.productAttributes,
  'addressResponsiblePerson',
)
const contactResponsiblePerson = getFirstAttributeLabel(
  props.productAttributes,
  'contactResponsiblePerson',
)
const contactTypeResponsiblePerson = getFirstAttributeLabel(
  props.productAttributes,
  'contactTypeResponsiblePerson',
)
const importerHref = computed(() =>
  getContactHref(contactTypeResponsiblePerson, contactResponsiblePerson),
)
const hasImporter = computed(
  () =>
    responsiblePersonEu || addressResponsiblePerson || contactResponsiblePerson,
)

// Helper function to get the href for the contact
const getContactHref = (type: string, contact: string) => {
  return type === 'mail' || contact.includes('@')
    ? `mailto:${contact}`
    : normalizeUrl(contact).toString()
}
</script>
